import { Environment, CameraControls, Float } from '@react-three/drei'
import PostProcessing from './components/PostProcessing.jsx'
import { useControls, folder, Leva } from 'leva'
import { useEffect, useMemo, useRef, useCallback } from 'react'
import { track } from '@vercel/analytics'

// Track page access on component mount
track('experience_accessed')

import useGame from './stores/useGame.jsx'
import * as THREE from'three'
import { useThree } from '@react-three/fiber'

// Hoisted constants — stable references, never re-created
const WHITE = new THREE.Color('white')

import { F35 } from './components/F35.jsx'
import { Jet } from './components/Jet.jsx'
import { MissileSmokeEmitter } from './components/MissileSmokeEmitter.jsx'
import { MachDiamonds } from './components/MachDiamonds.jsx'
import { Contrails } from './components/Contrails.jsx'

import { CameraAnimator } from './components/CameraAnimator.jsx'
import { FloatIntensityController } from './components/FloatIntensityController.jsx'
import { MarkerRepulsion } from './components/MarkerRepulsion.jsx'
import UI from './UI.jsx'
import { useDeviceType } from './hooks/useDeviceType.js'

export default function Experience()
{
  useThree()

  // Device detection
  const { deviceType, orientation: detectedOrientation } = useDeviceType()
  const setDeviceType = useGame((state) => state.setDeviceType)
  const setDeviceOrientation = useGame((state) => state.setDeviceOrientation)

  // Sync device type to store
  useEffect(() => {
    setDeviceType(deviceType)
    setDeviceOrientation(detectedOrientation)
  }, [deviceType, detectedOrientation, setDeviceType, setDeviceOrientation])

  const touched = useGame((state) => state.touched)
  const selectedSec = useGame((state) => state.selectedSec)
  const floatIntensityMultiplier = useGame((state) => state.floatIntensityMultiplier)

  const cameraControlsRef = useRef()
  const pjdamBoneRef = useRef(null)
  const handleBoneReady = useCallback((bone) => { pjdamBoneRef.current = bone }, [])

  const pjdamFireActive = useGame((state) => state.pjdamFireActive)

  // Jet (billboard) controls
  const { jetPlumeIntensity } = useControls('Jet', {
    jetPlumeIntensity: { value: 1.10, min: 0, max: 2, step: 0.05, label: 'Plume Intensity' },
  })

  // MachDiamonds (ray-march) controls
  const {
    nozzleRadius, pressure,
    plumeIntensity, plumeBrightness, plumeOpacity, diamondBrightness, diamondHue,
    turbulence, speed, posX, posY, decayFromTip,
  } = useControls('Mach Diamonds', {
    'Plume': folder({
      nozzleRadius:    { value: 1.81,  min: 0.05, max: 10,    step: 0.005, label: 'Nozzle Radius' },
      plumeIntensity:  { value: 0.16,  min: 0,    max: 3,     step: 0.01,  label: 'Intensity' },
      plumeBrightness: { value: 3.46,  min: 0,    max: 5,     step: 0.01,  label: 'Brightness' },
      plumeOpacity:    { value: 0.23,  min: 0,    max: 1,     step: 0.01,  label: 'Opacity' },
      turbulence:      { value: 1.00,  min: 0,    max: 1,     step: 0.01,  label: 'Turbulence' },
      speed:           { value: -900,  min: -1000, max: 1000, step: 1,     label: 'Flow Speed' },
      posX:            { value: -240,  min: -240, max: 0,     step: 0.5,   label: 'Position X' },
      posY:            { value: -1.3,  min: -120, max: 120,   step: 0.1,   label: 'Position Y' },
    }),
    'Shock Diamonds': folder({
      pressure:          { value: 0.05,  min: 0.001, max: 1.0, step: 0.001, label: 'Pressure Ratio' },
      diamondBrightness: { value: 300.0, min: 0,     max: 300, step: 0.5,   label: 'Brightness' },
      diamondHue:        { value: 0.38,  min: 0,     max: 1.0, step: 0.01,  label: 'Colour (blue→purple→orange)' },
      decayFromTip:      { value: 1,     min: 0,     max: 1,   step: 1,     label: 'Decay from tip (0) or nozzle (1)' },
    }),
  })

  // Disable zoom and pan on mobile portrait — rotate only
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px) and (orientation: portrait)')
    const update = () => {
      if (!cameraControlsRef.current) return
      if (mql.matches) {
        cameraControlsRef.current.dollySpeed = 0
        cameraControlsRef.current.truckSpeed = 0
      } else {
        cameraControlsRef.current.dollySpeed = 1
        cameraControlsRef.current.truckSpeed = 1
      }
    }
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [cameraControlsRef])

  // Permanently disable all pan/truck input — rotate and dolly only, on all devices
  useEffect(() => {
    if (!cameraControlsRef.current) return
    const cc = cameraControlsRef.current
    const ACTION = cc.constructor.ACTION
    // Mouse: right-click → NONE, middle-click drag → NONE
    cc.mouseButtons.right  = ACTION.NONE
    cc.mouseButtons.middle = ACTION.NONE
    // Touch: two-finger → dolly only (no truck), three-finger → NONE
    cc.touches.two   = ACTION.TOUCH_DOLLY
    cc.touches.three = ACTION.NONE
  }, [cameraControlsRef])

  // Lock ALL camera interaction during Weapon Systems (SEC001/SEC005/SEC006) or Weapon Stores (SEC006)
  useEffect(() => {
    if (!cameraControlsRef.current) return
    const isMobilePortrait = window.matchMedia('(max-width: 767px) and (orientation: portrait)').matches
    const lockAll = selectedSec === 'SEC001' || selectedSec === 'SEC005' || selectedSec === 'SEC006'
    cameraControlsRef.current.azimuthRotateSpeed = lockAll ? 0 : 1
    cameraControlsRef.current.polarRotateSpeed   = lockAll ? 0 : 1
    cameraControlsRef.current.dollySpeed         = lockAll ? 0 : isMobilePortrait ? 0 : 1
    cameraControlsRef.current.truckSpeed         = lockAll ? 0 : isMobilePortrait ? 0 : 1
  }, [selectedSec, cameraControlsRef])

  // Map diamondHue 0→1 through blue → purple → orange
  const diamondColor = useMemo(() => {
    const blue   = new THREE.Color(0.0, 0.4,  1.0)
    const purple = new THREE.Color(0.6, 0.2,  1.0)
    const orange = new THREE.Color(1.0, 0.45, 0.0)
    const c = new THREE.Color()
    if (diamondHue < 0.5) {
      c.lerpColors(blue, purple, diamondHue / 0.5)
    } else {
      c.lerpColors(purple, orange, (diamondHue - 0.5) / 0.5)
    }
    return c
  }, [diamondHue])

  return (
    <>
    <Leva hidden />
    <directionalLight 
        color={WHITE}
        position={[-1.4, 5, 3]} 
        intensity={ 3 } 
        castShadow 
        shadow-mapSize={[4096, 4096]}
        shadow-bias={[-0.000005]}
        shadow-camera-top={1}
        shadow-camera-bottom={-1}
        shadow-camera-left={-1}
        shadow-camera-right={1}
    />

    <CameraControls 
        ref={cameraControlsRef}
        minDistance={120}
        maxDistance={250}
        smoothTime={1}
        makeDefault
        onStart={touched}
    />

    <Environment 
        files={['/textures/sky_2.webp','/textures/sky_2-gainmap.webp', '/textures/sky_2.json']}
        background={true} 
        backgroundIntensity={1.0}
        environmentIntensity={1.0}    
        backgroundRotation={[0, -Math.PI /1.5 ,0]}
        environmentRotation={[0, -Math.PI /1.5 ,0]}  
        flipX ={true}                     
      />

    <Float
        speed={4}
        rotationIntensity={0.25 * floatIntensityMultiplier}
        floatIntensity={3 * floatIntensityMultiplier}
        floatingRange={[-1, 1]}
      >

        <F35 
            position={[-10,0,0]}
            rotation={[0,0,0]}
            scale={10.0}
            onBoneReady={handleBoneReady}
        />

        {/* Billboard jet plume */}
        <Jet  
          position={[-40, -1.25, 0]} 
          rotation={[Math.PI /2, 0, Math.PI /2*2]} 
          scale={[180, 18, 18]}
          plumeIntensity={jetPlumeIntensity}
        />

        {/* Ray-marched Mach diamonds — layered on top of the billboard plume */}
        <MachDiamonds
          position={[-260, -1.25, 0]} 
          rotation={[0, 0, 0]} 
          scale={[180, 6, 6]}
          nozzleRadius={nozzleRadius}
          pressure={pressure}
          plumeIntensity={plumeIntensity}
          plumeBrightness={plumeBrightness}
          plumeOpacity={plumeOpacity}
          decayFromTip={decayFromTip}
          diamondBrightness={diamondBrightness}
          turbulence={turbulence}
          speed={speed}
          colorDiamond={diamondColor}
        />

        {/* Contrails VFX - ribbon shader with distortion */}
        <Contrails
          position={[-20, -0, 65]} 
          rotation={[Math.PI /2, 0, Math.PI /2*2]} 
          scale={[100, 5, 5]}
          color="#c0e2fc"
          opacity={0.1}
        />

          <Contrails
          position={[-20, -0, -65]} 
          rotation={[Math.PI /2, 0, Math.PI /2*2]} 
          scale={[100, 5, 5]}
          color="#c0e2fc"
          opacity={0.1}
        />

    </Float>

    {/* Missile smoke — rendered at scene root so world-space particle positions are not double-transformed */}
    {pjdamFireActive && (
      <MissileSmokeEmitter
        boneObject={pjdamBoneRef.current}
        nozzleLocalPosition={[0.0, -1.65, 0]}
      />
    )}

    {/* Camera Animator */}
    <CameraAnimator cameraControlsRef={cameraControlsRef} />
    
    {/* Float Intensity Controller */}
    <FloatIntensityController />

    {/* Marker Repulsion — screen-space collision avoidance for SEC/POI markers */}
    <MarkerRepulsion />

    <PostProcessing />
    </>
  )

}
